import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../../hooks/use-dialog-state/UseDialogState";
import {Box, List, ListItem, ListItemText, ListSubheader, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, type JSX } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "../../../components/forms/form/Form";
import ResponsiveDialog from "../../../components/responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../../../components/unsaved-changes-prompt/UnsavedChangesPrompt";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import NotifyMeUtils, { NotifyMeFormValuesType } from "./NotifyMeUtils";
import { RecipientTypeEnum } from "../../../common/enums/RecipientTypeEnum";
import { useAvailableRecipientsToNotifyForUser } from "../../../queries/admin-reports/UseAvailableRecipientsToNotifyForUser";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { IRecipientNotification } from "../../../services/admin-report-data-service/Models";
import FormCheckbox from "../../../components/forms/form-checkbox/FormCheckbox";
import { Email, SmsOutlined, Phone } from "@mui/icons-material";
import NoData from "../../../components/no-data/NoData";
import { UseSaveRecipientsToNotify } from "../../../queries/admin-reports/UseSaveRecipientsToNotify";

export type NotifyMeDialogInitValues = {
    contextGroupID: number
};

export type NotifyMeDialogResult = DialogResult<void>;
export type NotifyMeDialogProps = DialogProps<NotifyMeDialogInitValues, NotifyMeDialogResult>;

export default function NotifyMeDialog({
    open,
    onClose,
    values,
}: NotifyMeDialogProps): JSX.Element {
    
    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    const theme = useTheme();

    const {data, status } = useQueryNotifications(useAvailableRecipientsToNotifyForUser)({
        contextGroupID: values?.contextGroupID as number,
        enabled: values?.contextGroupID !== undefined
    });

    const { mutate: saveRecipientsToNotify } = useQueryNotifications(UseSaveRecipientsToNotify)({
        groupID: values?.contextGroupID as number,
        onSuccess: async (data) => {
            
            acknowledge(t('disabledRecipientsReport.notifyme.saved'));
            onClose({
                reason: "ok"
            });
        }
    });

    const form = useForm<NotifyMeFormValuesType>({
        mode: "onChange",
        defaultValues: {
            userRecipients: []
        }
    });

    const {
        control,
        //setValue,
        getValues,
        handleSubmit,
        formState: {
            isDirty,
            isValid
        },
        reset,
        //watch,
        //clearErrors,
        //trigger
    } = form;

    const {
        fields: userRecipients,
        //append,
        //remove:
        //update
    } = useFieldArray({
        control: control,
        name: 'userRecipients',
        keyName: "fieldKey"
    });    

    function onOkay(_data: NotifyMeFormValuesType) {

        const recipientsToNotify = getValues('userRecipients');

        saveRecipientsToNotify(recipientsToNotify);

        onClose && onClose({
            reason: "ok",
        });

        reset();
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ 
                reason: "cancel" 
            });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    useEffect(() => {
        if (open && data) {
            const formData = NotifyMeUtils.mapToForm(data);
            reset(formData);
        }
    }, [
        open,
        values,
        data,
        reset
    ]);


    function getRecipientIcon(recipient: IRecipientNotification) {

        return (

            <Box sx={{
                position: "relative",
                display: 'flex',
                marginRight: 1,
                }}
            >
                { 
                    recipient.recipientType === RecipientTypeEnum.Email ?
                        <Email /> 
                    :
                    recipient.recipientType === RecipientTypeEnum.Text ?
                        <SmsOutlined /> :
                        <Phone />
                }

            </Box>
        )
    }

    return (
        <>
            {open ? 
                <>
                <ResponsiveDialog
                    title={t('disabledRecipientsReport.notifyme.title')}
                    enableOkay={isDirty && isValid}
                    okayLabel={t("buttons.save")}
                    open={open}
                    onClose={
                        async (reason: string) => {
                            reason === "ok"
                                ? handleSubmit(async (data) => onOkay(data))()
                                : onCancel();
                        }
                    }
                >
                    <Typography component="span" variant="body1">
                        {t("disabledRecipientsReport.notifyme.text")}
                    </Typography>


                    <Form form={form}>

                        {
                            status === 'success' && userRecipients.length === 0 &&
                            <NoData text={t('disabledRecipientsReport.notifyme.noRecipients')} />
                        }


                        <List sx={{
                                paddingTop: 0,
                                overflowX: "hidden",
                                display: 'flex',
                                flexDirection: 'column',
                                flex: '1 1 auto'        
                            }}
                            aria-labelledby="recipient-list-subheader"
                            subheader={
                                <ListSubheader id="recipient-list-subheader" component="div" >
                                    {t('disabledRecipientsReport.notifyme.instruction')}
                                </ListSubheader>
                            }
                        >
                            {userRecipients.map((o, index) =>

                                <ListItem key={index} disablePadding >

                                    <ListItemText primary={
                                        <Stack
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                flex: '1 1 auto',
                                                marginLeft: theme.spacing(2)
                                            }}>
                                            <FormCheckbox
                                                name={`userRecipients.${index}.isNotified`}
                                                label={<Stack direction='row'>{getRecipientIcon(o)} {o.recipientTo}</Stack>}
                                                sx={{ 
                                                    marginRight: 0,
                                                    marginTop: 0,
                                                }}
                                                typographyProps={{
                                                    sx: {
                                                        fontSize: '.95rem',
                                                    },
                                                }}
                                            />
                                        </Stack>
                                        } 
                                        slotProps={{
                                            primary: {
                                                sx: {
                                                    fontSize: '.9rem'
                                                }
                                            }
                                        }} 
                                    />

                                </ListItem>
                            )}                                      
                        </List>
                    </Form>
                </ResponsiveDialog>

                <UnsavedChangesPrompt
                    hasChanges={isDirty}
                    getUserConfirmation={getUserConfirmation}
                />

                </>
                : null 
                }
        </>
    );
}

export type NotifyMeDialogState = DialogState<NotifyMeDialogInitValues, NotifyMeDialogResult>;
export type BindNotifyMeDialogReturn = BindDialogReturn<NotifyMeDialogInitValues, NotifyMeDialogResult>;

export function useNotifyMeDialogState(): NotifyMeDialogState {
    return useDialogState();
}

export function bindNotifyMeDialog(props: NotifyMeDialogState): BindNotifyMeDialogReturn {
    return bindDialog(props);
}
